import React from 'react'
import { useIsMobile } from '@/hooks/use-mobile'

type SplitPaneProps = {
  left: React.ReactNode
  right: React.ReactNode
  initialLeftPercent?: number // 10-90
  minLeftPercent?: number
  maxLeftPercent?: number
  storageKey?: string
  className?: string
  /** On mobile, which pane to show first when stacked vertically */
  mobileOrder?: 'left-first' | 'right-first'
  /** On mobile, ratio of top pane (0.5 = 50/50 split, 0.3 = 30/70 split) */
  mobileTopRatio?: number
}

function SplitPane({
  left,
  right,
  initialLeftPercent = 60,
  minLeftPercent = 25,
  maxLeftPercent = 80,
  storageKey = 'splitpane:left',
  className,
  mobileOrder = 'left-first',
  mobileTopRatio = 0.4,
}: SplitPaneProps) {
  const isMobile = useIsMobile()
  const [leftPct, setLeftPct] = React.useState<number>(() => {
    if (typeof window === 'undefined') return initialLeftPercent
    const raw = window.localStorage.getItem(storageKey)
    const n = raw ? Number(raw) : initialLeftPercent
    return isFinite(n) ? Math.min(Math.max(n, minLeftPercent), maxLeftPercent) : initialLeftPercent
  })

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const dragging = React.useRef(false)

  const clamp = (p: number) => Math.min(Math.max(p, minLeftPercent), maxLeftPercent)

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const p = (x / rect.width) * 100
    const next = Math.round(clamp(p))
    setLeftPct(next)
  }
  const onPointerUp = (_e: React.PointerEvent) => {
    dragging.current = false
    try { window.localStorage.setItem(storageKey, String(leftPct)) } catch { /* ignore storage errors */ }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); setLeftPct(p => clamp(p - 2)) }
    if (e.key === 'ArrowRight') { e.preventDefault(); setLeftPct(p => clamp(p + 2)) }
  }

  // Mobile: Stack vertically instead of side-by-side
  if (isMobile) {
    const topContent = mobileOrder === 'left-first' ? left : right
    const bottomContent = mobileOrder === 'left-first' ? right : left
    const topPercent = Math.round(mobileTopRatio * 100)
    const bottomPercent = 100 - topPercent

    return (
      <div className={`relative w-full h-full flex flex-col ${className || ''}`}>
        <div
          className="overflow-auto border-b"
          style={{ height: `${topPercent}%` }}
        >
          {topContent}
        </div>
        <div
          className="overflow-auto flex-1"
          style={{ height: `${bottomPercent}%` }}
        >
          {bottomContent}
        </div>
      </div>
    )
  }

  // Desktop: Side-by-side with draggable divider
  return (
    <div ref={containerRef} className={`relative w-full h-full ${className || ''}`}>
      <div className="flex w-full h-full select-none">
        <div style={{ width: `${leftPct}%` }} className="min-w-[200px] overflow-auto">
          {left}
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="relative w-2 shrink-0 cursor-col-resize"
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-border" />
          <div className="absolute inset-y-0 -left-2 -right-2" />
        </div>
        <div style={{ width: `${100 - leftPct}%` }} className="min-w-[240px] overflow-auto">
          {right}
        </div>
      </div>
    </div>
  )
}

export default SplitPane
export { SplitPane }
